import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  GridRowModes,
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { createBustop, deleteBustop, updateBustop } from "../../api/bustop";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";

const BustopTab = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [newBustop, setNewBustop] = useState({ bustopname: "", rid: "", pickupcode: "", eid: "" });
  const [routes, setRoutes] = useState([]);
  const [emirates, setEmirates] = useState([]);

  useEffect(() => {
    fetchBustops();
    fetchEmirates();
  }, []);

  useEffect(() => {
    if (newBustop.eid) {
      fetchRoutesByEmirate(newBustop.eid);
    }
  }, [newBustop.eid]);

  const fetchBustops = async () => {
    try {
      const data = await customFetch("bustops");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching bus stops:", error);
    }
  };

  const fetchEmirates = async () => {
    try {
      const data = await customFetch("emirates");
      setEmirates(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchRoutesByEmirate = async (eid) => {
    try {
      const data = await customFetch(`routes/by-emirate/${eid}`);
      if (data.status && Array.isArray(data.data)) {
        setRoutes(data.data);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setNewBustop({ bustopname: "", rid: "", pickupcode: "", eid: "" });
  };

  const handleSubmitNewBustop = async () => {
    if (newBustop.bustopname && newBustop.rid && newBustop.pickupcode) {
      try {
        const response = await createBustop(newBustop);
        console.log("API Response:", response); // Debugging line
        if (response.status && response.data) {
          const newBustopData = Array.isArray(response.data) ? response.data[0] : response.data;
          const formattedNewBustop = {
            ...newBustopData,
            Route: routes.find((route) => route.rid === newBustopData.rid),
          };
          setRows((prevRows) => [...prevRows, formattedNewBustop]);
          handleCloseModal();
        } else {
          console.error("Unexpected response format:", response);
        }
      } catch (error) {
        console.error("Error creating bus stop:", error);
      }
    }
  };
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setSelectedRowId(id);
    setOpenConfirmDialog(true);
  };

  const handleDialogClose = (confirmed) => {
    setOpenConfirmDialog(false);
    if (confirmed) {
      handleConfirmDelete();
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteBustop(selectedRowId);
      setRows(rows.filter((row) => row.sid !== selectedRowId));
    } catch (error) {
      console.error("Error deleting area:", error);
    }
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    updateBustop(newRow.sid, updatedRow).then(() => {
      setRows(rows.map((row) => (row.sid === newRow.sid ? updatedRow : row)));
    });
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    { field: "bustopname", headerName: "Bus Stop Name", flex: 1, minWidth: 150, editable: true },
    { field: "pickupcode", headerName: "Pickup Code", width: 120, editable: true },
    {
      field: "routeName",
      headerName: "Route",
      width: 150,
      valueGetter: (params) => params.row.Route?.routename,
    },
    {
      field: "emirateName",
      headerName: "Emirate",
      width: 150,
      valueGetter: (params) => params.row.Route?.Area?.Emirate?.name,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 20, padding: "0 0px" }}>
                  Save
                </Button>
              }
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={
                <Button variant="text" sx={{ minWidth: 40, padding: "0 0px" }}>
                  Cancel
                </Button>
              }
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={handleOpenModal}>
          Add New Bus Stop
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.sid}
      />
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-bustop-modal"
        aria-describedby="modal-to-add-new-bustop"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 id="add-bustop-modal">Add New Bus Stop</h2>
          <TextField
            autoFocus
            margin="dense"
            id="bustopname"
            label="Bus Stop Name"
            type="text"
            fullWidth
            variant="standard"
            value={newBustop.bustopname}
            onChange={(e) => setNewBustop({ ...newBustop, bustopname: e.target.value })}
          />
          <TextField
            margin="dense"
            id="pickupcode"
            label="Pickup Code"
            type="text"
            fullWidth
            variant="standard"
            value={newBustop.pickupcode}
            onChange={(e) => setNewBustop({ ...newBustop, pickupcode: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="emirate-select-label">Emirate</InputLabel>
            <Select
              labelId="emirate-select-label"
              id="emirate-select"
              value={newBustop.eid}
              label="Emirate"
              onChange={(e) => setNewBustop({ ...newBustop, eid: e.target.value, rid: "" })}
            >
              {emirates?.map((emirate) => (
                <MenuItem key={emirate.eid} value={emirate.eid}>
                  {emirate.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="route-select-label">Route</InputLabel>
            <Select
              labelId="route-select-label"
              id="route-select"
              value={newBustop.rid}
              label="Route"
              onChange={(e) => setNewBustop({ ...newBustop, rid: e.target.value })}
              disabled={!newBustop.eid}
            >
              {routes.map((route) => (
                <MenuItem key={route.rid} value={route.rid}>
                  {route.routename}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitNewBustop} variant="contained">
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this Bustop? This action cannot be undone."
        onClose={handleDialogClose}
        onConfirm={() => handleDialogClose(true)}
      />
    </Box>
  );
};

export default BustopTab;
